/**
 * 4-Section Familiarisation Structure (WBC3 Compliant)
 * Streamlined approach based on WBC3 Appendix 8 actual requirements
 * Universal sections + task-specific familiarisation per Section 7.1
 */

import { generateEmergencyEquipmentChecklist } from './emergencyEquipmentProcedures.js';
import { generatePreSailingFamiliarisationChecklist } from './preSailingFamiliarisation.js';
import { generateVesselSystemsControlsChecklist } from './vesselSystemsControls.js';
import { generateTaskSpecificFamiliarisationChecklist } from './taskSpecificFamiliarisation.js';
import { generateRoleSpecificProceduresChecklist } from './roleSpecificProcedures.js';
import { generateComprehensiveFamiliarisation } from './familiarisationAdapter.js';
import { transformVesselConfig } from '../vesselConfigMapping.js';

// Section 1: Pre-Sailing Familiarisation (WBC3 Appendix 8, Section 9)
export const SECTION_1_PRE_SAILING_FAMILIARISATION = {
  id: 'pre_sailing_familiarisation',
  title: 'Pre-Sailing Familiarisation',
  description: 'Essential safety briefing before vessel departure for ALL persons onboard',
  reference: 'Pre-departure Safety Briefing',
  order: 1,
  mandatory: true,
  blocks_operations: true,
  
  generateChecklist: generatePreSailingFamiliarisationChecklist
};

// Section 2: Emergency Equipment & Procedures (from GPT prompt)
export const SECTION_2_EMERGENCY_EQUIPMENT = {
  id: 'emergency_equipment_procedures',
  title: 'Emergency Equipment & Procedures', 
  description: 'Location and operation of all emergency equipment',
  reference: 'Life-saving appliances familiarisation',
  order: 2,
  mandatory: true,
  
  generateChecklist: generateEmergencyEquipmentChecklist
};

// Section 3: Vessel Systems & Controls (WBC3 Appendix 8, Section 7.2)
export const SECTION_3_VESSEL_SYSTEMS = {
  id: 'vessel_systems_controls',
  title: 'Vessel Systems & Controls',
  description: 'Universal vessel systems, mooring procedures, watertight doors',
  reference: 'General Familiarisation',
  order: 3,
  mandatory: true,
  
  generateChecklist: generateVesselSystemsControlsChecklist
};

// Section 4: Task-Specific Familiarisation (WBC3 Appendix 8, Section 7.1)
export const SECTION_4_TASK_SPECIFIC = {
  id: 'task_specific_familiarisation',
  title: 'Task-Specific Familiarisation',
  description: 'Equipment and operation-specific training based on vessel configuration',
  reference: 'Training Appropriate to Tasks',
  order: 4,
  mandatory: false, // Only appears if vessel has specific equipment/operations
  
  generateChecklist: generateTaskSpecificFamiliarisationChecklist
};

// Section 5: Role-Specific Procedures (WBC3 Appendix 8, Section 7.1 + SMS)
export const SECTION_5_ROLE_SPECIFIC = {
  id: 'role_specific_procedures',
  title: 'Role-Specific Procedures',
  description: 'Role-based operational procedures and SMS-driven obligations',
  reference: 'Role-Based Training & SMS',
  order: 5,
  mandatory: true,
  
  generateChecklist: generateRoleSpecificProceduresChecklist
};


// Export 5-section familiarisation structure
export const FIVE_SECTION_FAMILIARISATION = [
  SECTION_1_PRE_SAILING_FAMILIARISATION,
  SECTION_2_EMERGENCY_EQUIPMENT,
  SECTION_3_VESSEL_SYSTEMS,
  SECTION_4_TASK_SPECIFIC,
  SECTION_5_ROLE_SPECIFIC
];

// Legacy export for backward compatibility
export const FOUR_SECTION_FAMILIARISATION = FIVE_SECTION_FAMILIARISATION;

// Legacy exports for backward compatibility
export const EIGHT_SECTION_FAMILIARISATION = FOUR_SECTION_FAMILIARISATION;
export const SEVEN_SECTION_FAMILIARISATION = FOUR_SECTION_FAMILIARISATION;

/**
 * Main function to generate 5-section familiarisation
 * Based on WBC3 Appendix 8 actual requirements
 * Universal sections + task-specific + role-specific procedures
 */
export function generateFiveSectionFamiliarisation(vessel, crew) {
  // Try to use comprehensive items from regulations assistant
  if (typeof transformVesselConfig === 'function' && typeof generateComprehensiveFamiliarisation === 'function') {
    try {
      const transformedVessel = transformVesselConfig(vessel);
      const comprehensiveResult = generateComprehensiveFamiliarisation(transformedVessel, crew);
      
      // Add metadata to match expected format
      return {
        familiarisationType: 'five_section_wbc3_comprehensive',
        sections: comprehensiveResult.sections,
        totalSections: comprehensiveResult.sections.length,
        vessel: vessel,
        crew: crew,
        generatedAt: new Date().toISOString()
      };
    } catch (error) {

      // Fall through to legacy implementation
    }
  }
  const sections = [];
  
  FIVE_SECTION_FAMILIARISATION.forEach(section => {
    // Generate checklist for each section
    const checklist = section.generateChecklist(vessel, crew);
    
    // Skip task-specific section if no items
    if (section.id === 'task_specific_familiarisation' && checklist.items.length === 0) {
      return;
    }
    
    // Add section metadata
    checklist.order = section.order;
    checklist.mandatory = section.mandatory;
    checklist.blocks_operations = section.blocks_operations || false;
    checklist.requires_master_signoff = section.requires_master_signoff || false;
    checklist.conditional = section.conditional || false;
    
    sections.push(checklist);
  });
  
  return {
    familiarisationType: 'five_section_wbc3',
    sections: sections,
    totalSections: sections.length,
    vessel: vessel,
    crew: crew,
    generatedAt: new Date().toISOString()
  };
}

// Legacy function for backward compatibility
export function generateFourSectionFamiliarisation(vessel, crew) {
  return generateFiveSectionFamiliarisation(vessel, crew);
}

// Legacy function names for backward compatibility
export function generateEightSectionFamiliarisation(vessel, crew) {
  return generateFourSectionFamiliarisation(vessel, crew);
}

export function generateSevenSectionFamiliarisation(vessel, crew) {
  return generateFourSectionFamiliarisation(vessel, crew);
}
